// Platform-neutral state machine. The host supplies a monotonic millisecond clock and polls tick().
// No React, timers, audio APIs, storage, network or business completion writes belong here.
export function createTrainingEngine({ plan, clock, sessionId, countdownMs = 3000, cueFreshnessMs = 2000 }) {
  if (!plan?.sections?.length || typeof clock?.now !== "function" || !sessionId || ![0, 3000].includes(countdownMs)) throw new TypeError("Invalid coach configuration");
  let state = "ready", pausedFrom = null, pauseReason = null, lastClock = null;
  let activeMs = 0, sectionIndex = -1, sectionStartMs = 0, completedRepetitions = 0;
  let run = 0, sequence = 0, droppedCues = 0, outbox = [];
  let now = 0, targetMs = 0;
  const countdownEndMs = countdownMs;
  const emitted = new Set();
  const externalIds = new Set();
  const setEndIndices = new Map();
  plan.sections.forEach((phase, index) => {
    if (phase.setNumber && ["work", "sprint", "repetitions"].includes(phase.kind)) setEndIndices.set(phase.setNumber, index);
  });
  const completedSets = () => [...setEndIndices.values()].filter(index => sectionIndex > index).length;
  const active = () => state === "running" || state === "countdown";
  const runId = () => `${sessionId}:${run}`;
  const section = () => plan.sections[sectionIndex] || null;
  const progress = () => {
    if (state === "completed") return 1;
    if (sectionIndex < 0) return 0;
    const phase = section();
    const value = plan.totalDurationMs !== null
      ? Math.max(0, activeMs - countdownMs) / plan.totalDurationMs
      : (sectionIndex + (phase?.durationMs ? Math.min(1, Math.max(0, activeMs - sectionStartMs) / phase.durationMs) : 0)) / plan.sections.length;
    return Math.max(0, Math.min(0.999999, value));
  };
  const snapshot = () => Object.freeze({ state, pauseReason, sessionId: runId(), sectionIndex, section: section(),
    countdownActive: state === "countdown" || (state === "paused" && pausedFrom === "countdown"),
    nextSection: plan.sections[sectionIndex + 1] || null,
    elapsedMs: Math.max(0, activeMs - countdownMs),
    sessionElapsedMs: activeMs, remainingMs: section() && section().durationMs !== null ? Math.max(0, section().durationMs - (activeMs - sectionStartMs)) : null,
    countdownRemainingMs: Math.max(0, countdownEndMs - activeMs), currentRepetition: section()?.repetition ?? null,
    repetitions: plan.repetitions, completedRepetitions, remainingRepetitions: plan.repetitions - completedRepetitions,
    totalDurationMs: plan.totalDurationMs, progress: progress(), progressMode: plan.totalDurationMs === null ? "sections" : "time",
    currentSet: section()?.setNumber ?? (state === "completed" ? plan.totalSets : null), totalSets: plan.totalSets ?? null,
    currentInterval: section()?.intervalNumber ?? null, intervalsPerSet: section()?.intervalsPerSet ?? null,
    completedSets: completedSets(), remainingSets: plan.totalSets ? plan.totalSets - completedSets() : null,
    remainingIntervalsInSet: section()?.intervalsPerSet ? Math.max(0, section().intervalsPerSet - section().intervalNumber
      + (["work", "sprint", "repetitions"].includes(section().kind) ? 1 : 0)) : null, droppedCues });

  function emit(type, atMs, current = section(), params = {}) {
    const sectionId = current?.id || "countdown";
    const key = `${run}:${type}:${sectionId}`;
    if (emitted.has(key)) return;
    emitted.add(key);
    const emittedAtMs = now - Math.max(0, targetMs - atMs);
    const cue = Object.freeze({ id: `${runId()}:${++sequence}`, sessionId: runId(), type, sectionId, atMs, emittedAtMs, textKey: type,
      tone: type.startsWith("COUNTDOWN_") ? "countdown" : type === "SESSION_COMPLETE" ? "complete"
        : type.endsWith("_END") ? "end" : type.endsWith("_START") ? "start" : "none",
      params: Object.freeze({ kind: current?.kind, multipleSections: plan.sections.length > 1,
        announcementKind: current?.kind === "warmup" && plan.sections[plan.sections.indexOf(current) + 1]?.kind === "sprint" ? "sprint_warmup" : current?.kind,
        repetition: current?.repetition, repetitions: plan.repetitions, setNumber: current?.setNumber, intervalNumber: current?.intervalNumber,
        remainingRepetitions: plan.repetitions - completedRepetitions, durationMs: current?.durationMs, distanceM: current?.distanceM, ...params }) });
    if (now - emittedAtMs > cueFreshnessMs) droppedCues += 1;
    else outbox.push(cue);
  }
  function enterSection() {
    if (sectionIndex >= plan.sections.length) {
      state = "completed";
      emit("SESSION_COMPLETE", activeMs, plan.sections[plan.sections.length - 1]);
      return;
    }
    sectionStartMs = activeMs;
    const current = section();
    emit(current.kind === "set_rest" ? "SET_REST_START" : current.kind === "rest" ? "REST_START" : ["work", "sprint", "repetitions"].includes(current.kind) ? "INTERVAL_START" : "SECTION_START", activeMs);
  }
  function finishSection() {
    const current = section();
    if (["work", "sprint", "repetitions"].includes(current.kind)) {
      completedRepetitions += 1;
      emit("INTERVAL_END", activeMs);
      if (completedRepetitions < plan.repetitions) emit("INTERVALS_REMAINING", activeMs);
    } else if (["rest", "set_rest"].includes(current.kind)) emit("REST_END", activeMs);
    sectionIndex += 1;
    // Distance completion is confirmed now: enter the next full phase immediately.
    // No invented sprint duration or extra transition time; stopwatch continues.
    enterSection();
  }
  function thresholds(from, to, start, duration, upcoming) {
    if (!upcoming) return; // Completion has one tone, no final countdown.
    const markers = [[3000, "COUNTDOWN_3"], [2000, "COUNTDOWN_2"], [1000, "COUNTDOWN_1"]];
    for (const [remaining, type] of markers) {
      const at = start + duration - remaining;
      if (at >= start && at > from && at <= to) {
        emit(type, at, upcoming);
      }
    }
  }
  function readClock() {
    const value = clock.now();
    if (!Number.isFinite(value)) return null;
    return value;
  }
  function tick() {
    const value = readClock();
    if (value === null || (lastClock !== null && value < lastClock)) {
      if (active()) { pausedFrom = state; state = "paused"; pauseReason = "clock"; outbox = []; }
      lastClock = value;
      return snapshot();
    }
    now = value;
    const delta = lastClock === null ? 0 : now - lastClock;
    lastClock = now;
    if (!active()) return snapshot();
    targetMs = activeMs + delta;
    if (state === "countdown") {
      const end = Math.min(targetMs, countdownEndMs);
      // Initial preparation tone was emitted when this countdown began.
      for (const [remaining, type] of [[2000, "COUNTDOWN_2"], [1000, "COUNTDOWN_1"]]) {
        const at = countdownEndMs - remaining;
        if (at > activeMs && at <= end) emit(type, at, section() || plan.sections[0]);
      }
      activeMs = end;
      if (activeMs < countdownEndMs) return snapshot();
      state = "running"; if (sectionIndex < 0) sectionIndex = 0; enterSection();
    }
    while (state === "running") {
      const current = section();
      if (current.durationMs === null) { activeMs = targetMs; break; }
      const end = Math.min(targetMs, sectionStartMs + current.durationMs);
      thresholds(activeMs, end, sectionStartMs, current.durationMs, plan.sections[sectionIndex + 1]);
      activeMs = end;
      if (activeMs < sectionStartMs + current.durationMs) break;
      finishSection();
    }
    return snapshot();
  }
  function start() {
    if (state !== "ready") return snapshot();
    const value = readClock();
    if (value === null) return snapshot();
    now = value; lastClock = now; targetMs = 0; run += 1;
    state = countdownMs ? "countdown" : "running";
    emit("SESSION_START", 0, null);
    if (countdownMs) emit("COUNTDOWN_3", 0, plan.sections[0]);
    else { sectionIndex = 0; enterSection(); }
    return snapshot();
  }
  function pause(reason = "manual") {
    tick();
    if (active()) {
      pausedFrom = state; state = "paused"; pauseReason = reason; outbox = [];
    }
    return snapshot();
  }
  function resume() {
    if (state !== "paused") return snapshot();
    const value = readClock();
    if (value === null) return snapshot();
    lastClock = value; now = value; state = pausedFrom; pausedFrom = null; pauseReason = null;
    return snapshot();
  }
  function abort() {
    if (["running", "countdown", "paused"].includes(state)) { tick(); state = "aborted"; outbox = []; }
    return snapshot();
  }
  function reset() {
    state = "ready"; pausedFrom = null; pauseReason = null; lastClock = null;
    activeMs = 0; targetMs = 0; sectionIndex = -1; sectionStartMs = 0; completedRepetitions = 0;
    droppedCues = 0; outbox = [];
    emitted.clear(); externalIds.clear();
    return snapshot();
  }
  function nextSection() {
    tick();
    if (state === "running" && section()?.durationMs === null && ["sprint", "repetitions"].includes(section()?.kind)) { targetMs = activeMs; finishSection(); }
    return snapshot();
  }
  // Future native host must verify source/workout identity before invoking this narrow signal.
  function acceptDistanceEvent(event) {
    tick();
    if (state !== "running" || section()?.kind !== "sprint" || !event?.id || externalIds.has(event.id)
        || event.sessionId !== runId() || event.sectionId !== section().id || event.type !== "DISTANCE_COMPLETE"
        || !Number.isFinite(event.distanceM) || event.distanceM < section().distanceM) return false;
    externalIds.add(event.id); targetMs = activeMs; finishSection(); return true;
  }
  function drainCues() {
    const value = readClock();
    const fresh = outbox.filter(cue => value !== null && value >= cue.emittedAtMs && value - cue.emittedAtMs <= cueFreshnessMs);
    droppedCues += outbox.length - fresh.length; outbox = []; return fresh;
  }
  return Object.freeze({ start, tick, pause, resume, abort, reset, nextSection, acceptDistanceEvent, drainCues, getSnapshot: snapshot });
}
